import React, { useEffect, useRef } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Pagination } from 'is24-corecss'
import type { Swiper as SwiperType } from 'swiper'
import 'swiper/css'
import { AttachmentDataWithRequiredAttachment } from '../../../../../../../../../../@types/types'
import { PictureCard } from './PictureCard'
import { CarouselIndicators } from './CarouselIndicators'
import { CarouselActionButtons } from './CarouselActionButtons'
import styles from './PictureCarousel.module.css'

interface PictureCarouselProps {
  attachments: AttachmentDataWithRequiredAttachment[]
  onEditClick: (attachment: AttachmentDataWithRequiredAttachment) => void
  onAddClick: () => void
  selectedImageId: string | undefined
  onImageSelect: (imageId: string) => void
  showAddButton?: boolean
  dropzoneId: string
}

export const PictureCarousel: React.FC<PictureCarouselProps> = ({
  attachments,
  onEditClick,
  onAddClick,
  selectedImageId,
  onImageSelect,
  showAddButton = true,
  dropzoneId,
}) => {
  const swiperRef = useRef<SwiperType | null>(null)
  const selectedSlide = attachments.findIndex(({ attachment }) => attachment.id === selectedImageId)
  const validSelectedSlide = Math.max(selectedSlide, 0)

  useEffect(() => {
    if (swiperRef.current?.activeIndex !== validSelectedSlide) {
      swiperRef.current?.slideTo(validSelectedSlide)
    }
  }, [validSelectedSlide])

  if (attachments.length === 0) {
    return null
  }

  const currentAttachment = attachments[validSelectedSlide]
  const totalPictures = attachments.length

  const handleSlideChange = (swiper: SwiperType) => {
    const attachment = attachments[swiper.activeIndex]
    if (attachment) onImageSelect(attachment.attachment.id)
  }

  const handlePageChange = (index: number) => {
    const attachment = attachments[index]
    if (attachment) onImageSelect(attachment.attachment.id)
    swiperRef.current?.slideTo(index)
  }

  return (
    <div className={styles.carouselContainer}>
      <Swiper
        spaceBetween={0}
        slidesPerView={1}
        loop={false}
        onSwiper={(swiper) => {
          swiperRef.current = swiper
        }}
        onSlideChange={handleSlideChange}
        className={styles.carouselSlider}
      >
        {attachments.map((attachment, index) => (
          <SwiperSlide key={attachment.attachment?.url || index}>
            <PictureCard attachment={attachment} onClick={() => onEditClick(attachment)} />
          </SwiperSlide>
        ))}
      </Swiper>

      <CarouselIndicators
        currentSlide={validSelectedSlide}
        totalPictures={totalPictures}
        currentAttachment={currentAttachment}
      />

      <CarouselActionButtons
        currentAttachment={currentAttachment}
        dropzoneId={dropzoneId}
        onEditClick={onEditClick}
        onAddClick={onAddClick}
        showAddButton={showAddButton}
      />

      {totalPictures > 1 && (
        <div className={styles.paginationContainer}>
          <Pagination
            type='dot'
            variant='on-image'
            pages={totalPictures}
            activePage={validSelectedSlide}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </div>
  )
}
